import React, { useState } from 'react';
import { motion } from 'framer-motion';

// Image imports
const cbyonHero1 = '/images/cbyon-hero-1.png';
const cbyonHero2 = '/images/cbyon-hero-2.png';
const cbyonHero3 = '/images/cbyon-hero-3.png';
const cbyonHero4 = '/images/cbyon-hero-4.png';
const cbyonHero5 = '/images/cbyon-hero-5.png';
const cbyonOldApp = '/images/cbyon-old-app.png';
const cbyonNotebook1 = '/images/cbyon-notebook-1.png';
const cbyonNotebook2 = '/images/cbyon-notebook-2.png';
const cbyonNotebook3 = '/images/cbyon-notebook-3.png';
const cbyonNotebook4 = '/images/cbyon-notebook-4.png';
const cbyonDesignElements = '/images/cbyon-design-elements.png';
const cbyonFeature1 = '/images/cbyon-feature-1.png';
const cbyonFeature2 = '/images/cbyon-feature-2.png';
const cbyonFeature3 = '/images/cbyon-feature-3.png';
const cbyonFeature4 = '/images/cbyon-feature-4.png';

// Video imports
const cbyonPrototype = '/video/cbyon-prototype.mp4';
const cbyonViewOpacitySlider = '/video/cbyon-view-opacity-slider.mp4';
const cbyonViewModeOrientationMenu = '/video/cbyon-view-mode-orientation-menu-2.mp4';
const cbyonViewTypeDatasetMenu = '/video/cbyon-view-type-dataset-menu.mp4';

const CBYON: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <div className="min-h-screen text-black dark:text-white">
      <div className="mx-auto xl:max-w-8xl flex flex-1 flex-col gap-2 p-4 md:p-6">
        <section className="grid md:grid-cols-12 my-4 md:my-8">
          <div className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-6 md:space-y-12">
            <motion.h1
              className="md:!leading-tight text-3xl md:text-4xl"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              CBYON Eclipse ranks among the top three surgical navigation tools in clinical feedback.
            </motion.h1>
            <motion.div
              className="grid md:grid-cols-12 md:gap-6 space-y-4 md:space-y-0 col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="md:col-span-5 space-y-4 md:space-y-8">
                <div className="space-y-1 md:space-y-2">
                  <p className="font-mono-recursive md:text-lg">Team</p>
                  <div className="md:text-lg dark:dark:text-zinc-400">
                  <a href="https://joeshock.com/" target="_blank" rel="noopener noreferrer" className='transition text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline cursor-pointer'>Joe Shock</a><span className="mx-2">·</span>1x Product Manager<span className="mx-2">·</span>4x Software Engineers<span className="mx-2">·</span>2x Hardware Engineers
                  </div>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Tools</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">Figma<span className="mx-2">·</span>Axure RP<span
                      className="mx-2">·</span>Origami Studio</p>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Date</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">2019 - 2026</p>
                </div>
              </div>
              <div className="md:col-span-7 space-y-4 md:space-y-8">
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Role & Contributions</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">Product Designer<span className="mx-1"> · </span>User
                    Research<span className="mx-1"> · </span>Prototyping</p>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Key Achievements</p>
                   <p className="md:text-lg dark:dark:text-zinc-400">With the Q1 2025 release of CBYON 4.0, clinical feedback confirms CBYON Eclipse as one of the top three navigation tools—easier to use than Brainlab, more versatile than Medtronic. <button 
                     onClick={openModal}
                     className="inline-flex transition text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline cursor-pointer"
                   >
                     Watch Dr. Nangiana's testimony video
                   </button> to see the software, hardware, and surgeon feedback in action.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="mt-8 md:mt-14">
          <motion.div
            className="grid md:grid-cols-3 gap-12 md:gap-28"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="col-span-3 grid md:grid-cols-12 gap-4 md:grid-rows-2">
              <div
                className="md:col-span-4 md:row-span-2 flex flex-col justify-center overflow-hidden rounded-lg md:rounded-2xl">
                <div className="h-full w-full object-cover">
                  <img className="h-full w-full object-cover object-center" src={cbyonHero1} alt="CBYON Hero 1" />
                </div>
              </div>
              <div className="md:col-span-5 flex flex-col justify-center overflow-hidden rounded-lg md:rounded-2xl">
                <div className="h-full w-full object-cover">
                  <img className="h-full w-full object-cover object-center" src={cbyonHero2} alt="CBYON Hero 2" />
                </div>
              </div>
              <div className="md:col-span-3 flex flex-col justify-center overflow-hidden rounded-lg md:rounded-2xl">
                <div className="h-full w-full object-cover">
                  <img className="h-full w-full object-cover object-center" src={cbyonHero3} alt="CBYON Hero 3" />
                </div>
              </div>
              <div className="md:col-span-4 flex flex-col justify-center overflow-hidden rounded-lg md:rounded-2xl">
                <div className="h-full w-full object-cover">
                  <img className="h-full w-full object-cover object-center" src={cbyonHero4} alt="CBYON Hero 4" />
                </div>
              </div>
              <div className="md:col-span-4 flex flex-col justify-center overflow-hidden rounded-lg md:rounded-2xl">
                <div className="h-full w-full object-cover">
                  <img className="h-full w-full object-cover object-center" src={cbyonHero5} alt="CBYON Hero 5" />
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-12 mt-12 md:mt-28 space-y-12 md:space-y-28">
            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Project Overview</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">CBYON is a cutting-edge surgical guidance technology. Despite its technological advancements, the app faced usability challenges that hindered adoption and efficiency in the operating room. My task was to redesign the app to enhance usability, reduce cognitive load, and improve surgical precision. The project spanned over two years, during which I served as the Lead Product Designer, using tools like Figma, Axure RP, and Origami Studio.</p>
            </motion.div>

            <motion.div
              className="col-span-12 flex flex-col justify-center bg-zinc-100 dark:bg-zinc-900 overflow-hidden rounded-lg md:rounded-2xl"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="flex justify-center w-full text-center p-12 md:p-24">
                <p className="max-w-prose lg:leading-normal text-md md:text-lg dark:text-zinc-300">The <span
                    className="text-zinc-500 dark:text-white">original CBYON app had usability issues</span> that hindered operating room
                  adoption. Its cluttered interface made accessing critical controls difficult, navigation was
                  unintuitive, robotic arm controls were complex, and 3D visualization lacked precision.</p>
              </div>
              <img src={cbyonOldApp} alt="Original CBYON app interface" />
            </motion.div>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Goals and Objectives</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">The redesign aimed to simplify the user
                interface to reduce cognitive load during surgeries, improve real-time 3D visualization
                capabilities,
                streamline robotic arm control for intuitive operation, and ensure compliance with medical UI safety
                standards.</p>
            </motion.div>

            <motion.div
              className="col-span-12 grid md:grid-cols-12 gap-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div
                className="md:col-span-6 md:row-span-4 flex flex-col justify-center overflow-hidden rounded-lg md:rounded-2xl bg-[#07070A]">
                <img src={cbyonNotebook1} alt="CBYON notebook 1" />
              </div>
              <div
                className="md:col-span-6 md:row-span-3 flex flex-col justify-center overflow-hidden rounded-lg md:rounded-2xl bg-[#07070A]">
                <img src={cbyonNotebook2} alt="CBYON notebook 2" />
              </div>
              <div
                className="md:col-span-6 md:row-span-4 flex flex-col justify-center overflow-hidden rounded-lg md:rounded-2xl bg-[#07070A]">
                <img src={cbyonNotebook4} alt="CBYON notebook 4" />
              </div>
              <div
                className="md:col-span-6 md:row-span-3 flex flex-col justify-center overflow-hidden rounded-lg md:rounded-2xl bg-[#07070A]">
                <img src={cbyonNotebook3} alt="CBYON notebook 3" />
              </div>
            </motion.div>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Design Process</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">I restructured the app's navigation to
                create a streamlined information hierarchy. Wireframes were developed to visualize the new layout,
                emphasizing quick access to essential controls and real-time metrics.</p>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">The interface was designed with a
                minimalist
                approach to reduce visual noise, focusing on high-contrast color schemes for better visibility,
                dynamic
                interactive 3D anatomical models, and a clean dashboard that displayed only the most critical
                surgical
                data.</p>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">Low-fidelity prototypes were developed
                to
                test key interactions, transitioning to high-fidelity prototypes that simulated real-world
                conditions.
                I
                created a robust design system including color and value tokens for consistent UI elements,
                interactive
                components for robotic controls, and medical-standard iconography.</p>
            </motion.div>

            <motion.div
              className="col-span-12 flex flex-col justify-center bg-zinc-100 dark:bg-zinc-900 overflow-hidden rounded-lg md:rounded-2xl"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div
                className="flex flex-col md:flex-row justify-center w-full space-y-12 md:space-y-0 md:space-x-16 p-12 md:p-24">
                <div className="md:w-3/12">
                  <p className="max-w-prose lg:leading-normal text-md md:text-lg dark:text-zinc-300">I created a <span
                      className="dark:text-white">prototype</span> to <span className="dark:text-white">demonstrate system
                      navigation</span> and <span className="dark:text-white">touch interactions</span> for cranial point
                    collecting, highlighting an intuitive workflow and precise gestures for improved surgical
                    efficiency.</p>
                </div>
                <div className="md:w-9/12">
                  <div className="flex min-h-full flex-col justify-center bg-black overflow-hidden rounded-lg">
                    <div className="aspect-video">
                      <video className="w-full h-full" autoPlay loop muted playsInline>
                        <source src={cbyonPrototype} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div
                className="flex md:min-h-full md:h-80 flex-col justify-center bg-black overflow-hidden rounded-lg md:rounded-2xl">
                <div className="aspect-video">
                  <video className="w-full h-full" autoPlay loop muted playsInline>
                    <source src={cbyonViewOpacitySlider} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
              <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
                Ergonomical opactity slider
              </figcaption>
            </motion.figure>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Key Challenges and Solutions</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">One challenge was the complexity of
                robotic
                arm controls. I developed gesture-based controls that allowed surgeons to adjust robotic arms with
                minimal interaction. Visual clutter during procedures was another issue. I implemented a
                context-aware
                UI that dynamically displayed only relevant information based on the surgical phase. Inefficient 3D
                visualization was addressed by integrating an advanced rendering engine for smoother, more accurate
                anatomical models.</p>
            </motion.div>

            <motion.div
              className="col-span-12 grid md:grid-cols-12 gap-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="md:col-span-6">
                <figure className="bg-black overflow-hidden rounded-lg md:rounded-2xl">
                  <div className="flex min-h-full flex-col justify-center">
                    <div className="sm:px-6 lg:col-span-12 lg:px-0">
                      <div className="p-6">
                        <video className="w-full h-full" autoPlay loop muted playsInline>
                          <source src={cbyonViewModeOrientationMenu} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    </div>
                  </div>
                </figure>
              </div>
              <div className="md:col-span-6">
                <figure className="bg-black overflow-hidden rounded-lg md:rounded-2xl">
                  <div className="flex min-h-full flex-col justify-center">
                    <div className="sm:px-6 lg:col-span-12 lg:px-0">
                      <div className="p-6">
                        <video className="w-full h-full" autoPlay loop muted playsInline>
                          <source src={cbyonViewTypeDatasetMenu} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    </div>
                  </div>
                </figure>
              </div>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg md:rounded-lg md:rounded-2xl"
                src={cbyonDesignElements} alt="CBYON components" />
              <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
                CBYON components
              </figcaption>
            </motion.figure>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Final Solution</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">The redesigned CBYON app delivered a
                seamless and intuitive experience for surgeons, enhancing their ability to perform precise and
                efficient
                ENT procedures. Key features included gesture-based robotic controls, real-time 3D visualization,
                and a
                context-aware interface that minimized cognitive load during procedures.</p>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg md:rounded-2xl" src={cbyonFeature1} alt="CBYON Feature 1" />
            </motion.figure>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg md:rounded-2xl" src={cbyonFeature2} alt="CBYON Feature 2" />
            </motion.figure>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg md:rounded-2xl" src={cbyonFeature3} alt="CBYON Feature 3" />
            </motion.figure>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg md:rounded-2xl" src={cbyonFeature4} alt="CBYON Feature 4" />
            </motion.figure>
          </div>
        </section>
      </div>

      {/* Video Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="relative w-full max-w-4xl mx-4">
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 text-2xl font-bold"
            >
              ×
            </button>
            <div className="relative w-full h-96 md:h-[500px] rounded-lg overflow-hidden">
              <iframe 
                src="https://streamable.com/e/0tipvt" 
                title="Dr. Nangiana testimony video"
                width="100%" 
                height="100%" 
                frameBorder="0" 
                allowFullScreen 
                style={{width: '100%', height: '100%', position: 'absolute', left: '0px', top: '0px', overflow: 'hidden'}}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CBYON;
